import { escapeHTML } from "./utils.js";

// ─── Texto con formato (negrilla, subrayado, color, tamaño) ──────────────────
// Los campos de texto libre de los documentos guardaban texto plano y se
// imprimían escapados. Para poder resaltar una parte —solo lo que el usuario
// selecciona con el mouse— hay que guardar HTML, y eso obliga a sanearlo: lo
// que se guarda hoy termina impreso mañana, y nadie debe poder meter código
// por ahí (ni a propósito ni pegando desde Word).
//
// La regla es lista blanca: sobrevive SOLO el formato que ofrecemos. Todo lo
// demás se degrada a su texto, nunca se pierde el contenido.

const TAGS_OK = new Set(["B", "STRONG", "I", "EM", "U", "BR", "SPAN", "DIV", "P"]);
const ESTILOS_OK = new Set(["color", "font-size", "font-weight", "text-decoration", "background-color"]);

// Equivalencias del <font size="1..7"> que todavía emiten algunos navegadores.
const TAM_FONT = { "1": "10px", "2": "12px", "3": "14px", "4": "17px", "5": "21px", "6": "28px", "7": "40px" };

// Los navegadores escriben los colores como rgb(...) / rgba(...), así que hay
// que dejar pasar esa función —y solo esa— antes de prohibir los paréntesis.
const COLOR_FN = /rgba?\(\s*[\d.,%\s/]+\)/gi;

function estiloSeguro(valor) {
  const salida = [];
  String(valor || "").split(";").forEach(par => {
    const i = par.indexOf(":");
    if (i < 0) return;
    const prop = par.slice(0, i).trim().toLowerCase();
    const val = par.slice(i + 1).trim();
    if (!ESTILOS_OK.has(prop) || !val) return;
    // Nada de url(), expression() ni escapes: solo valores simples de color y
    // tamaño. Si trae algo raro, se descarta la propiedad entera.
    if (/[;{}()<>]|url|expression|javascript|@import/i.test(val.replace(COLOR_FN, "color"))) return;
    salida.push(prop + ":" + val);
  });
  return salida.join(";");
}

function limpiarNodo(nodo, doc) {
  Array.from(nodo.childNodes).forEach(n => {
    if (n.nodeType === 3) return;                 // texto: se queda tal cual
    if (n.nodeType !== 1) { n.remove(); return; } // comentarios y demás: fuera

    // <font> es lo que emiten algunos navegadores al cambiar color/tamaño: se
    // traduce a <span> con estilo en vez de perder el formato.
    if (n.tagName === "FONT") {
      const span = doc.createElement("span");
      const est = [];
      if (n.getAttribute("color")) est.push("color:" + n.getAttribute("color"));
      if (TAM_FONT[n.getAttribute("size")]) est.push("font-size:" + TAM_FONT[n.getAttribute("size")]);
      if (est.length) span.setAttribute("style", estiloSeguro(est.join(";")));
      while (n.firstChild) span.appendChild(n.firstChild);
      n.replaceWith(span);
      limpiarNodo(span, doc);
      return;
    }

    if (!TAGS_OK.has(n.tagName)) {                // etiqueta no permitida:
      n.replaceWith(doc.createTextNode(n.textContent || ""));  // se conserva su texto
      return;
    }

    Array.from(n.attributes).forEach(a => { if (a.name !== "style") n.removeAttribute(a.name); });
    if (n.hasAttribute("style")) {
      const est = estiloSeguro(n.getAttribute("style"));
      if (est) n.setAttribute("style", est); else n.removeAttribute("style");
    }
    limpiarNodo(n, doc);

    // Un <span> que se quedó sin estilo no pinta nada: se deja solo su
    // contenido para no acumular basura cada vez que se edita la nota.
    if (n.tagName === "SPAN" && !n.hasAttribute("style")) {
      while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
      n.remove();
    }
  });
}

// Deja el HTML listo para guardar o imprimir. El texto plano de siempre pasa
// intacto (escapado), así que los documentos viejos siguen viéndose igual.
export function sanitizarTextoRico(html) {
  if (html == null) return "";
  const bruto = String(html);
  if (!bruto) return "";
  if (typeof DOMParser === "undefined") return escapeHTML(bruto);   // fuera del navegador
  try {
    const doc = new DOMParser().parseFromString("<div id=\"r\"></div>", "text/html");
    const raiz = doc.getElementById("r");
    raiz.innerHTML = bruto;
    limpiarNodo(raiz, doc);
    return raiz.innerHTML;
  } catch {
    return escapeHTML(bruto);
  }
}

// ¿El valor trae HTML, o es texto plano de toda la vida? Decide cómo pintarlo.
export function tieneFormato(html) {
  return /<(b|strong|i|em|u|span|br|div|p|font)\b/i.test(String(html || ""));
}

// ¿Trae formato de verdad (negrilla, color, subrayado…)? Los saltos de línea
// que el navegador escribe como <div> o <br> NO cuentan: un texto sin resaltar
// se sigue guardando plano, igual que siempre.
export function tieneEstilo(html) {
  return /<(b|strong|i|em|u|span|font)\b/i.test(String(html || ""));
}

// Lo que se guarda en el documento: HTML solo si el usuario resaltó algo.
export function normalizarTextoRico(html) {
  const limpio = sanitizarTextoRico(html);
  if (!tieneEstilo(limpio)) {
    const plano = textoPlano(limpio).replace(/ /g, " ");
    return plano.trim() ? plano : "";
  }
  return limpio;
}

// Para pintar en pantalla o en la impresión: el texto plano de siempre se
// escapa (documentos viejos intactos), el que trae formato se sanea.
export function htmlParaMostrar(valor) {
  const s = String(valor == null ? "" : valor);
  return tieneFormato(s) ? sanitizarTextoRico(s) : escapeHTML(s);
}

// Texto sin formato: para buscadores, exportaciones a Excel y cualquier sitio
// donde el HTML estorbaría.
export function textoPlano(html) {
  const s = String(html || "");
  if (!tieneFormato(s)) return s;
  if (typeof DOMParser === "undefined") return s.replace(/<[^>]*>/g, "");
  try {
    const doc = new DOMParser().parseFromString("<div id=\"r\"></div>", "text/html");
    const raiz = doc.getElementById("r");
    raiz.innerHTML = s;
    raiz.querySelectorAll("br").forEach(br => br.replaceWith(doc.createTextNode("\n")));
    // Cada bloque (lo que el navegador crea al pulsar Enter) es un salto de línea.
    raiz.querySelectorAll("div,p").forEach(b => b.before(doc.createTextNode("\n")));
    return (raiz.textContent || "").replace(/^\n+/, "");
  } catch {
    return s.replace(/<[^>]*>/g, "");
  }
}
